import {
  LinearGlobalConstraintRule as Constraint,
  LinearGlobalConstraintData as ConstraintData
} from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import './LinearConstraints.scss';
import * as Dfns from 'date-fns/fp';
import { useEffect, useMemo, useState } from 'react';
import { ModalEditWidget } from '@Components/ModalEditWidget/ModalEditWidget';
import { ConstraintSaveBar } from '@Components/ConstraintSaveBar/ConstraintSaveBar';
import { useConstraintDraft } from '@src/utils/hooks/useConstraintDraft';
import { applyTableChanges, withIndexes } from '@src/utils/optimization-utils';
import { SaveChangesInput } from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useSetError } from '../../redux/modals';
import {
  awaitJSON,
  LinearLambdaFetch,
  LinearOptimizationsLambdaFetch
} from '../../utils/fetch-utils';

const PRETTY_DATE_FORMAT = 'yyyy-MM-dd hh:mma';

interface Rotation {
  rotation_name: string;
  day_set: string;
  daypart_begin: string;
  daypart_end: string;
  avail: string;
  network: string;
  status: string;
  fullName?: string;
}

const persistConstraint = async (data: ConstraintData): Promise<ConstraintData> =>
  awaitJSON<ConstraintData>(
    await LinearOptimizationsLambdaFetch('/constraints', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: data
    })
  );

export const LinearConstraints = () => {
  const setError = useSetError();
  const [buyableNetworkAvailRotations, setBuyableNetworkAvailRotations] = useState<
    Record<string, Record<string, Record<string, boolean>>> | undefined
  >();
  const [rotationsByNetwork, setRotationsByNetwork] = useState<
    Record<string, Rotation[]> | undefined
  >();

  useEffect(() => {
    if (buyableNetworkAvailRotations) return;

    (async () => {
      try {
        const linearRotationsAndPricing: Rotation[] = await awaitJSON(
          await LinearLambdaFetch('/get_rates')
        );

        const grouped: Record<string, Record<string, Record<string, boolean>>> = {};
        for (const row of linearRotationsAndPricing.filter(r => r.status === 'buyable')) {
          grouped[row.network] ??= {};
          grouped[row.network][row.avail] ??= {};
          grouped[row.network][row.avail][
            `${row.rotation_name} (${row.day_set} ${row.daypart_begin} - ${row.daypart_end})`
          ] = true;
        }
        setBuyableNetworkAvailRotations(grouped);

        const rotationsByNetworkResult: Record<string, Rotation[]> = {};
        linearRotationsAndPricing
          .filter(
            rotation =>
              (rotation.status === 'buyable' || rotation.status === 'secured') &&
              Dfns.isValid(
                Dfns.parseISO(
                  `2016-01-01T${rotation.daypart_begin.length === 4 ? '0' : ''}${rotation.daypart_begin}:00`
                )
              ) &&
              Dfns.isValid(
                Dfns.parseISO(
                  `2016-01-01T${rotation.daypart_end.length === 4 ? '0' : ''}${rotation.daypart_end}:00.000000`
                )
              )
          )
          .forEach(rotation => {
            const start = Dfns.parseISO(
              `2016-01-01T${rotation.daypart_begin.length === 4 ? '0' : ''}${rotation.daypart_begin}:00`
            );
            let end = Dfns.parseISO(
              `2016-01-01T${rotation.daypart_end.length === 4 ? '0' : ''}${rotation.daypart_end}:00.000000`
            );
            while (Dfns.getMinutes(end) % 30 !== 0) {
              end = Dfns.addMinutes(1, end);
            }
            const fullName = `${rotation.rotation_name} (${rotation.day_set} ${Dfns.format('h:mma', start)}-${Dfns.format('h:mma', end)})`;
            (rotationsByNetworkResult[rotation.network] ??= []).push({ ...rotation, fullName });
          });
        setRotationsByNetwork(rotationsByNetworkResult);
      } catch (e) {
        setError({
          message: `Failed to load network rotation info: ${e.message}`,
          reportError: e
        });
      }
    })();
  }, [buyableNetworkAvailRotations, setError]);

  const [initialData, setInitialData] = useState<ConstraintData | undefined>();
  useEffect(() => {
    if (initialData) return;

    (async () => {
      const lambdaData: ConstraintData = await awaitJSON(
        await LinearOptimizationsLambdaFetch('/constraints', {
          params: {
            company: 'global',
            name: 'global'
          }
        })
      );

      for (const table of ['maximum', 'avoid'] as const) {
        lambdaData.constraints[table] = withIndexes(
          lambdaData.constraints[table]
            .map(row => ({ ...row, lastmodified: row.lastmodified || '2020-01-01T00:00:00-08:00' }))
            .sort((a, b) => b.lastmodified.localeCompare(a.lastmodified))
        );
      }
      setInitialData(lambdaData);
    })();
  }, [initialData]);

  const { data, update, discard, save, changeCount, canSave, saving } = useConstraintDraft({
    identity: 'linear-global',
    initialData,
    persist: persistConstraint
  });

  const applyTable = (section: 'maximum' | 'avoid', changes: SaveChangesInput<Constraint>) => {
    if (!data) return;
    update({
      ...data,
      constraints: {
        ...data.constraints,
        [section]: withIndexes(
          applyTableChanges(
            data.constraints[section] ?? [],
            changes.addedData,
            changes.modifiedData,
            changes.deletedData
          )
        )
      }
    });
  };

  const maximumHeaders = [
    {
      type: 'select',
      field: 'network',
      label: 'Network',
      isMulti: false,
      searchable: false,
      options: 'network',
      clearKeys: ['avail', 'rotation'],
      modalRow: 0,
      modalFlex: 2
    },
    {
      type: 'select',
      field: 'reach',
      label: 'Avail',
      searchable: false,
      options: 'avail',
      clearKeys: ['rotation'],
      modalRow: 0,
      modalWidth: 100
    },
    {
      type: 'select',
      field: 'rotation',
      label: 'Rotation',
      isMulti: false,
      searchable: false,
      width: 300,
      options: 'networkRotation',
      modalRow: 1,
      modalFlex: 2
    },
    {
      type: 'numeric',
      field: 'length15sUnits',
      label: '15s units',
      width: 120,
      modalRow: 2,
      modalFlex: 2
    },
    {
      type: 'numeric',
      field: 'length30sUnits',
      label: '30s units',
      width: 120,
      modalRow: 2,
      modalFlex: 3
    },
    {
      type: 'numeric',
      field: 'length60sUnits',
      label: '60s units',
      width: 120,
      modalRow: 2,
      modalFlex: 4
    },
    {
      type: 'currency',
      field: 'spend',
      label: 'Spend',
      width: 120,
      modalRow: 3,
      modalFlex: 1
    },
    {
      type: 'text',
      field: 'notes',
      label: 'Notes',
      flex: 1,
      modalRow: 4,
      modalFlex: 1
    },
    {
      type: 'timestamp',
      field: 'lastmodified',
      label: 'Last Modified',
      width: 200,
      modalRow: 5,
      modalFlex: 1,
      readOnly: true
    }
  ];

  const avoidHeaders = [
    {
      type: 'select',
      field: 'network',
      label: 'Network',
      isMulti: false,
      searchable: false,
      options: 'network',
      clearKeys: ['avail', 'rotation'],
      modalRow: 0,
      modalFlex: 2
    },
    {
      type: 'select',
      field: 'avail',
      label: 'Avail',
      searchable: false,
      options: 'avail',
      clearKeys: ['rotation'],
      modalRow: 0,
      modalWidth: 100
    },
    {
      type: 'select',
      field: 'rotation',
      label: 'Rotation',
      isMulti: false,
      searchable: false,
      width: 300,
      options: 'networkRotationWithoutEach',
      modalRow: 1,
      modalFlex: 2
    },
    {
      type: 'select',
      field: 'length',
      label: 'Length',
      searchable: false,
      options: 'length',
      modalRow: 1,
      modalWidth: 100
    },
    {
      type: 'text',
      field: 'notes',
      label: 'Notes',
      flex: 1,
      modalRow: 2,
      modalFlex: 1
    },
    {
      type: 'timestamp',
      field: 'lastmodified',
      label: 'Last Modified',
      width: 200,
      modalRow: 3,
      modalFlex: 1,
      readOnly: true
    }
  ];

  const lastMod = useMemo(() => {
    if (!data) return '';
    const username = (data.lastuser ?? '').replace(/@.*/, '');
    return `Last Modified: ${username} at ${Dfns.format(PRETTY_DATE_FORMAT)(Dfns.parseISO(data.lastmodified))}`;
  }, [data]);

  const selectorOptions = useMemo(
    () => ({
      rotation: [
        'Overnight',
        'Early Morning',
        'Daytime',
        'Early Fringe',
        'Prime',
        'Late Fringe',
        'Broad Rotator',
        'Weekend'
      ].map(e => ({ label: e, value: e })),
      days: ['Weekday', 'Weekend'].map(e => ({ label: e, value: e })),
      avail: ['N', 'L'].map(e => ({ label: e, value: e })),
      length: ['15', '30', '60'].map(e => ({ label: e, value: e })),
      vingtiles: Array.from({ length: 20 }, (_, i) => i + 1).map(e => ({ label: e, value: e })),
      network: [
        { label: 'Each Network', value: 'Each Network' },
        ...Object.keys(buyableNetworkAvailRotations ?? {}).map(key => ({ value: key, label: key }))
      ],
      networkRotation: Object.fromEntries(
        Object.entries(rotationsByNetwork ?? {}).map(([key, list]) => [
          key,
          [
            { value: 'Each Rotation', label: 'Each Rotation' },
            ...list.map(row => ({ value: row.fullName ?? '', label: row.fullName ?? '' }))
          ]
        ])
      ),
      networkRotationWithoutEach: Object.fromEntries(
        Object.entries(rotationsByNetwork ?? {}).map(([key, list]) => [
          key,
          list.map(row => ({ value: row.fullName ?? '', label: row.fullName ?? '' }))
        ])
      )
    }),
    [buyableNetworkAvailRotations, rotationsByNetwork]
  );

  return (
    <Page
      title='Linear Global Constraints'
      pageType='Linear Global Constraints'
      subHeader={
        data && (
          <ConstraintSaveBar
            changeCount={changeCount}
            disabled={!canSave}
            saving={saving}
            onSave={save}
            onDiscard={discard}
          />
        )
      }
      minHeight='600px'
      actions={data ? <div data-testid='last-modified'>{lastMod}</div> : undefined}
    >
      {data?.constraints ? (
        <div className='linear-constraints'>
          <ModalEditWidget<Constraint>
            className='constraint-table'
            headers={maximumHeaders}
            name='Maximum constraints'
            dataTestId='maximum-constraints-table'
            initialTableData={data.constraints.maximum}
            filterBar
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            onSaveModal={changes => applyTable('maximum', changes)}
            selectorOptions={selectorOptions}
          />
          <ModalEditWidget<Constraint>
            className='constraint-table'
            headers={avoidHeaders}
            name='Avoid constraints'
            dataTestId='avoid-constraints-table'
            initialTableData={data.constraints.avoid}
            filterBar
            saveOnEdit
            saveButtonText='Apply'
            readOnly={saving}
            onSaveModal={changes => applyTable('avoid', changes)}
            selectorOptions={selectorOptions}
          />
        </div>
      ) : (
        <FullPageSpinner />
      )}
    </Page>
  );
};
